import {useState, useRef, useCallback} from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Stack,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Avatar,
  Divider,
  Tooltip,
  Fade,
  InputAdornment
} from "@mui/material";
import {createTheme, ThemeProvider, alpha} from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FormatSizeIcon from "@mui/icons-material/FormatSize";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import properties from "constants/ApplicationConstants";
import axios from "axios";
import {BASE_URL} from "APIclient";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {main: "#5C6BC0", dark: "#3949AB", light: "#7986CB"},
    secondary: {main: "#26A69A"},
    background: {default: "#F0F2F8", paper: "#FFFFFF"},
    success: {main: "#43A047", light: "#E8F5E9"},
    error: {main: "#E53935"},
    warning: {main: "#FB8C00", light: "#FFF3E0"},
    text: {primary: "#1A1F36", secondary: "#6B7280"}
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    h4: {fontWeight: 700, letterSpacing: "-0.5px"},
    h6: {fontWeight: 600},
    subtitle2: {fontWeight: 600, letterSpacing: "0.04em"}
  },
  shape: {borderRadius: 12},
  components: {
    MuiTextField: {
      defaultProps: {variant: "outlined", size: "small", fullWidth: true},
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            backgroundColor: "#F8F9FC",
            "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: "#5C6BC0"},
            "&.Mui-focused": {backgroundColor: "#fff"}
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {borderRadius: 10, textTransform: "none", fontWeight: 600, fontSize: 14},
        containedPrimary: {
          background: "linear-gradient(135deg, #5C6BC0 0%, #3949AB 100%)",
          boxShadow: "0 4px 14px rgba(92,107,192,0.35)",
          "&:hover": {boxShadow: "0 6px 18px rgba(92,107,192,0.45)"}
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {borderRadius: 16, boxShadow: "0 2px 20px rgba(0,0,0,0.06)"}
      }
    },
    MuiChip: {
      styleOverrides: {root: {borderRadius: 8, fontWeight: 500}}
    }
  }
});

const ALLOWED_TYPES = ["image/jpeg", "image/gif", "image/bmp", "image/png"];
const MAX_FILES = 1;

const toHex = val =>
  Math.max(0, Math.min(255, parseInt(val) || 0))
    .toString(16)
    .padStart(2, "0");

const formatBytes = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function SectionLabel({icon, label}) {
  return (
    <Stack direction="row" alignItems="center" gap={1} mb={2}>
      <Box sx={{color: "primary.main", display: "flex"}}>{icon}</Box>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{textTransform: "uppercase", fontSize: 11, letterSpacing: "0.07em"}}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default function ImageUpload() {
  const [fontSize, setFontSize] = useState("");
  const [xCord, setXCord] = useState("");
  const [yCord, setYCord] = useState("");
  const [rValue, setRValue] = useState("");
  const [gValue, setGValue] = useState("");
  const [bValue, setBValue] = useState("");
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const r = Math.max(0, Math.min(255, parseInt(rValue) || 0));
  const g = Math.max(0, Math.min(255, parseInt(gValue) || 0));
  const b = Math.max(0, Math.min(255, parseInt(bValue) || 0));
  const hexColor = `#${toHex(rValue)}${toHex(gValue)}${toHex(bValue)}`;
  const hasColor = rValue !== "" || gValue !== "" || bValue !== "";
  const isLight = r * 0.299 + g * 0.587 + b * 0.114 > 128;

  const generatePreviews = fileList => {
    fileList.forEach((file, i) => {
      const reader = new FileReader();
      reader.onload = e =>
        setPreviews(prev => {
          const updated = [...prev];
          updated[i] = e.target.result;
          return updated;
        });
      reader.readAsDataURL(file);
    });
  };

  const addFiles = useCallback(incoming => {
    const valid = Array.from(incoming).filter(f => ALLOWED_TYPES.includes(f.type));
    if (valid.length === 0) return;
    const single = [valid[0]];
    setFiles(single);
    setPreviews([]);
    generatePreviews(single);
    setErrors(e => ({...e, files: ""}));
  }, []);

  const removeFile = () => {
    setFiles([]);
    setPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validate = () => {
    const errs = {};
    if (!fontSize) errs.fontSize = "Font size is required";
    if (!xCord) errs.xCord = "X coordinate is required";
    if (!yCord) errs.yCord = "Y coordinate is required";
    if (files.length === 0) errs.files = "Please select an image";
    return errs;
  };

  const resetForm = () => {
    setFontSize("");
    setXCord("");
    setYCord("");
    setRValue("");
    setGValue("");
    setBValue("");
    setFiles([]);
    setPreviews([]);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const token = window.sessionStorage.getItem("jwtToken");
    if (!token) {
      setErrors(e => ({...e, files: "Session expired. Please log in again."}));
      return;
    }

    setUploading(true);

    try {
      const url = `${BASE_URL}/admin/template/upload`;

      const formData = new FormData();
      formData.append("fontSize", fontSize);
      formData.append("xCord", xCord);
      formData.append("yCord", yCord);
      formData.append("red", rValue);
      formData.append("green", gValue);
      formData.append("blue", bValue);
      formData.append("file", files[0]);

      await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowSuccess(true);
      resetForm();

      setTimeout(() => setShowSuccess(false), 15000);
    } catch (error) {
      console.error("Upload error:", error);

      if (error.response?.status === 401) {
        setErrors(e => ({
          ...e,
          files: "Unauthorized (401). Your session may have expired. Please log in again."
        }));
      } else if (error.response?.status === 403) {
        setErrors(e => ({
          ...e,
          files: "Forbidden (403). You do not have permission to upload."
        }));
      } else {
        setErrors(e => ({
          ...e,
          files: `Upload failed: ${error.response?.data?.message || error.message}`
        }));
      }
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    resetForm();
    setShowSuccess(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ThemeProvider theme={theme}>
        <Box sx={{mt: 2, mb: 3}}>
          <Fade in={showSuccess} unmountOnExit>
            <Alert
              icon={<CheckCircleOutlineIcon />}
              severity="success"
              sx={{borderRadius: 3, fontWeight: 500, mb: 3}}
            >
              Image uploaded successfully! Processing has started.
            </Alert>
          </Fade>

          <Paper
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "grey.100",
              overflow: "hidden",
              width: "100%"
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2.5,
                background: "linear-gradient(135deg, #5C6BC0 0%, #3949AB 100%)",
                display: "flex",
                alignItems: "center",
                gap: 1.5
              }}
            >
              <CloudUploadIcon sx={{color: "rgba(255,255,255,0.9)", fontSize: 22}} />
              <Typography
                variant="h6"
                sx={{
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: properties.fontFamily
                }}
              >
                Image Upload Settings
              </Typography>
            </Box>

            {uploading && (
              <LinearProgress
                sx={{
                  height: 3,
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #5C6BC0, #26A69A)"
                  }
                }}
              />
            )}

            <Box p={{xs: 2, sm: 3}}>
              <Stack spacing={4}>
                <Box>
                  <SectionLabel
                    icon={<FormatSizeIcon sx={{fontSize: 16}} />}
                    label="Text Position & Size"
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Font Size (px)"
                        type="number"
                        placeholder="e.g. 24"
                        value={fontSize}
                        onChange={e => {
                          setFontSize(e.target.value);
                          setErrors(er => ({...er, fontSize: ""}));
                        }}
                        error={!!errors.fontSize}
                        helperText={errors.fontSize}
                        inputProps={{min: 1, max: 300}}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FormatSizeIcon sx={{fontSize: 16, color: "text.secondary"}} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="X Coordinate"
                        type="number"
                        placeholder="e.g. 100"
                        value={xCord}
                        onChange={e => {
                          setXCord(e.target.value);
                          setErrors(er => ({...er, xCord: ""}));
                        }}
                        error={!!errors.xCord}
                        helperText={errors.xCord}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MyLocationIcon sx={{fontSize: 16, color: "text.secondary"}} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Y Coordinate"
                        type="number"
                        placeholder="e.g. 50"
                        value={yCord}
                        onChange={e => {
                          setYCord(e.target.value);
                          setErrors(er => ({...er, yCord: ""}));
                        }}
                        error={!!errors.yCord}
                        helperText={errors.yCord}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MyLocationIcon sx={{fontSize: 16, color: "text.secondary"}} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <SectionLabel
                    icon={<PaletteOutlinedIcon sx={{fontSize: 16}} />}
                    label="Text Color (RGB)"
                  />
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Red (0–255)"
                        type="number"
                        placeholder="255"
                        value={rValue}
                        onChange={e => setRValue(e.target.value)}
                        inputProps={{min: 0, max: 255}}
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#E53935"
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Green (0–255)"
                        type="number"
                        placeholder="255"
                        value={gValue}
                        onChange={e => setGValue(e.target.value)}
                        inputProps={{min: 0, max: 255}}
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#43A047"
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Blue (0–255)"
                        type="number"
                        placeholder="255"
                        value={bValue}
                        onChange={e => setBValue(e.target.value)}
                        inputProps={{min: 0, max: 255}}
                        sx={{
                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#1E88E5"
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Tooltip
                        title={hasColor ? `rgb(${r}, ${g}, ${b})` : "Enter RGB values"}
                        arrow
                        placement="top"
                      >
                        <Box
                          sx={{
                            height: 40,
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: hasColor ? "transparent" : "grey.200",
                            bgcolor: hasColor ? `rgb(${r},${g},${b})` : "#EEEEEE",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 1.5,
                            cursor: "default",
                            transition: "background 0.2s",
                            boxShadow: hasColor ? `0 4px 12px rgba(${r},${g},${b},0.4)` : "none"
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: hasColor ? (isLight ? "#333" : "#fff") : "#999",
                              fontSize: 11,
                              letterSpacing: "0.04em"
                            }}
                          >
                            {hasColor ? hexColor.toUpperCase() : "Preview"}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>

                <Divider />

                <Box>
                  <SectionLabel
                    icon={<ImageOutlinedIcon sx={{fontSize: 16}} />}
                    label="Select Image"
                  />

                  {files.length === 0 && (
                    <Box
                      onDragOver={e => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={e => {
                        e.preventDefault();
                        setDragOver(false);
                        addFiles(e.dataTransfer.files);
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        border: "2px dashed",
                        borderColor: dragOver
                          ? "primary.main"
                          : errors.files
                          ? "error.main"
                          : "grey.300",
                        borderRadius: 3,
                        p: {xs: 3, sm: 5},
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: dragOver
                          ? alpha("#5C6BC0", 0.06)
                          : errors.files
                          ? alpha("#E53935", 0.04)
                          : "grey.50",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: alpha("#5C6BC0", 0.05)
                        }
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.gif,.bmp,.png"
                        style={{display: "none"}}
                        onChange={e => addFiles(e.target.files)}
                      />
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          mx: "auto",
                          mb: 2,
                          bgcolor: dragOver ? alpha("#5C6BC0", 0.15) : alpha("#5C6BC0", 0.08),
                          color: "primary.main",
                          transition: "all 0.2s",
                          transform: dragOver ? "scale(1.1)" : "scale(1)"
                        }}
                      >
                        <CloudUploadIcon sx={{fontSize: 30}} />
                      </Avatar>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color="text.primary"
                        gutterBottom
                      >
                        {dragOver ? "Release to upload" : "Drop an image here or click to browse"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Select <strong>1 file</strong> (JPG, JPEG, GIF, BMP, PNG)
                      </Typography>
                      <Stack
                        direction="row"
                        justifyContent="center"
                        flexWrap="wrap"
                        gap={0.75}
                        mt={1.5}
                      >
                        {[".jpg", ".jpeg", ".gif", ".bmp", ".png"].map(ext => (
                          <Chip
                            key={ext}
                            label={ext}
                            size="small"
                            sx={{
                              fontSize: 11,
                              height: 22,
                              bgcolor: "grey.100",
                              color: "text.secondary"
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {errors.files && (
                    <Typography variant="caption" color="error" sx={{mt: 0.75, display: "block"}}>
                      {errors.files}
                    </Typography>
                  )}

                  {files.length > 0 && (
                    <Fade in>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          border: "1px solid",
                          borderColor: "grey.100",
                          borderRadius: 2.5,
                          bgcolor: "grey.50",
                          mt: 1,
                          transition: "all 0.15s",
                          "&:hover": {
                            bgcolor: alpha("#5C6BC0", 0.04),
                            borderColor: "primary.light"
                          }
                        }}
                      >
                        {previews[0] ? (
                          <Box
                            component="img"
                            src={previews[0]}
                            alt={files[0].name}
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 2,
                              objectFit: "cover",
                              border: "1px solid",
                              borderColor: "grey.200",
                              flexShrink: 0
                            }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 2,
                              bgcolor: "grey.100",
                              flexShrink: 0
                            }}
                          >
                            <ImageOutlinedIcon sx={{color: "grey.400", fontSize: 20}} />
                          </Avatar>
                        )}
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={500} noWrap color="text.primary">
                            {files[0].name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{fontFamily: "monospace"}}
                          >
                            {formatBytes(files[0].size)}
                          </Typography>
                        </Box>
                        <Tooltip title="Remove file" arrow>
                          <IconButton
                            size="small"
                            onClick={removeFile}
                            sx={{
                              color: "text.secondary",
                              "&:hover": {color: "error.main", bgcolor: alpha("#E53935", 0.08)}
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Paper>
                    </Fade>
                  )}

                  {files.length > 0 && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.gif,.bmp,.png"
                        style={{display: "none"}}
                        onChange={e => addFiles(e.target.files)}
                      />
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => {
                          removeFile();
                          setTimeout(() => fileInputRef.current?.click(), 50);
                        }}
                        sx={{mt: 1, color: "primary.main", fontSize: 12}}
                      >
                        Change image
                      </Button>
                    </>
                  )}
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                bgcolor: "grey.50"
              }}
            >
              <Button
                variant="outlined"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                sx={{
                  borderColor: "grey.300",
                  color: "text.secondary",
                  "&:hover": {borderColor: "grey.400", bgcolor: "grey.100"}
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={!uploading && <SaveOutlinedIcon />}
                onClick={handleSubmit}
                disabled={uploading}
                sx={{px: 3, minWidth: 140}}
              >
                {uploading ? "Uploading…" : "Save Image"}
              </Button>
            </Box>
          </Paper>
        </Box>
      </ThemeProvider>
    </DashboardLayout>
  );
}
